import { createContext, useContext, type PropsWithChildren, type ComponentProps } from 'react';
import type {
  HeaderProps,
  TDrawerItem,
  HeaderVariantType,
  LogoThemeColors,
  HeaderLogoVariant,
} from '../types';
import type { LanguageSelector } from '@dx-ui/osc-language-selector';
import type { Surfaces } from '@dx-ui/global-ui-prism';

/**
 * Brand configuration for the header
 */
export type HeaderBrandConfig = HeaderProps['brand'];

/**
 * User authentication and profile information
 */
export type HeaderUserConfig = {
  user: HeaderProps['user'];
  onSignInAttempt: HeaderProps['onSignInAttempt'];
  onSignOut: HeaderProps['onSignOut'];
  loginOptions: HeaderProps['loginOptions'];
  analytics?: HeaderProps['analytics'];
};

/**
 * Visual and theming configuration
 */
export type HeaderVisualConfig = {
  surfaceType: Surfaces;
  isTranslucent?: boolean;
  isFluid: boolean;
  suppressLogo: boolean;
  heading?: string;
  isCondensedHeaderStyle?: boolean;
  isReimagineThemeExperience?: boolean;
};

/**
 * Feature flags and suppressions
 */
export type HeaderFeatureFlags = {
  shouldSuppressFindStay: boolean;
  shouldSuppressJoinAndSignIn: boolean;
  shouldOpenAccountLinksInNewWindow: boolean;
  shouldOpenJoinInNewWindow: boolean;
  shouldOpenJoinInPage?: boolean;
  shouldSuppressHonorsAccountLinks: boolean;
  shouldSuppressB2BLink?: boolean;
  shouldSuppressGoHiltonLink?: boolean;
  shouldSuppressHGVMaxLink?: boolean;
  shouldSuppressFriendsAndFamilyLink?: boolean;
};

/**
 * Navigation configuration
 */
export type HeaderNavigationConfig = {
  menuItems?: TDrawerItem[];
  globalNavLinks?: TDrawerItem[];
  mainNavLinks?: HeaderProps['mainNavLinks'];
  onGlobalNavigationClick?: HeaderProps['onGlobalNavigationClick'];
  isLegacyGlobalNavigationClickAnalyticsEnabled?: HeaderProps['isLegacyGlobalNavigationClickAnalyticsEnabled'];
  languageSelectorOptions?: ComponentProps<typeof LanguageSelector>;
  headerVariantType?: HeaderVariantType;
  globalNavCustomClassName?: string;
  bottomNavLinkAreaClassName?: string;
  navHeaderLinksLogoSurfaceType?: Surfaces;
  navHeaderLinksLogoColors?: LogoThemeColors;
  navHeaderLinksLogoVariant?: HeaderLogoVariant;
  globalNavLogoSurfaceType?: Surfaces;
  globalNavLogoColors?: LogoThemeColors;
  globalNavLogoVariant?: HeaderLogoVariant;
};

/**
 * Accessibility and app integration
 */
export type HeaderAccessibilityConfig = {
  wrapperId: string;
};

/**
 * Complete header context type
 */
export type HeaderContextValue = {
  brand: HeaderBrandConfig;
  user: HeaderUserConfig;
  visual: HeaderVisualConfig;
  features: HeaderFeatureFlags;
  navigation: HeaderNavigationConfig;
  accessibility: HeaderAccessibilityConfig;
};

const HeaderContext = createContext<HeaderContextValue | null>(null);

/**
 * Hook to access header context values
 * @throws Error if used outside of HeaderProvider
 */
export const useHeaderContext = () => {
  const context = useContext(HeaderContext);
  if (!context) {
    throw new Error('useHeaderContext must be used within a HeaderProvider');
  }
  return context;
};

export type HeaderProviderProps = PropsWithChildren<{
  value: HeaderContextValue;
}>;

/**
 * Provider component for header context
 * Wraps header variants and provides shared configuration to all child components
 */
export const HeaderProvider = ({ children, value }: HeaderProviderProps) => {
  return <HeaderContext.Provider value={value}>{children}</HeaderContext.Provider>;
};
